import iframeScript from "./iframeScript?raw";
import {addTranslation} from "@/services/subtitleService";
import {PersistedExtractionJob, TranslationResult as JobTranslationResult, TranslationSummary} from "@/types";
import {Subtitle} from "@/models/Subtitle";

const GOOGLE_TRANSLATE_URL = 'https://translate.google.com/translate_a/element.js?cb=';

type TranslationResult = {
    status: 'success' | 'error';
    error?: string;
    content: string[];
}

const languageAliases: Record<string, string> = {
    'he': 'iw',
    'zh-cn': 'zh-CN',
    'zh-tw': 'zh-TW',
    'pt-pt': 'pt-PT',
    'pt-br': 'pt',
}

export function translate(
    texts: string[],
    sourceLanguage: string|null,
    targetLanguage: string,
    onProgress?: (progress: number) => void,
    debug: boolean = false
): Promise<string[]> {
    return new Promise((resolve, reject) => {
        targetLanguage = languageAliases[targetLanguage] || targetLanguage;
        let result: string[] = [];
        const iframe = createIframeWithHTML(`
            <html>
                <head>
                <title>Subtitles</title>
                    <script src="https://code.jquery.com/jquery-3.7.1.min.js" integrity="sha256-/JqT3SQfawRcv/BIHPThkBvs0OEvtFFmqPF/lYI/Cxo=" crossorigin="anonymous"></script>
                    <script>
                        ${iframeScript.replace(/{targetLanguage}/g, targetLanguage).replace(/{sourceLanguage}/g, sourceLanguage || '')}
                    </script>
                    <script type="text/javascript" src="${GOOGLE_TRANSLATE_URL}on_loaded"></script>
                </head>
                <body>
                    <div id="google_element"></div>
                    <div id="content">
                        ${texts.map((text) => `<p>${text.replace(/\n/g, '<br/>')}</p>`).join('')}
                    </div>

                </body>
            </html>
        `, debug);

        const messageHandler = (event: MessageEvent) => {
            if(event.data.progress) {
                onProgress?.(event.data.progress);
            } else if (
                event instanceof MessageEvent &&
                event.data &&
                typeof event.data === 'object' &&
                'status' in event.data
            ) {
                window.removeEventListener('message', messageHandler);
                const res = event.data as TranslationResult;
                iframe.remove();
                if (res.status === 'success') {
                    resolve(res.content);
                } else {
                    reject(new Error(res.error));
                }
            }
        };

        window.addEventListener('message', messageHandler);
        return result;
    });
}

export function googleTranslate(
    jobs: PersistedExtractionJob[],
    jobSubtitles: Record<string, Subtitle[]>,
    targetLanguages: string[],
    sourceLanguage: string | null,
    onProgress: (progress: number) => void,
    signal: AbortSignal
): Promise<TranslationSummary> {
    return new Promise<TranslationSummary>(async (resolve, reject) => {
        try {
            const totalJobs = jobs.length * targetLanguages.length;
            let completedJobs = 0;

            const translationResults = new Map<number, JobTranslationResult>();
            jobs.forEach(job => {
                translationResults.set(job.id, {
                    jobId: job.id,
                    jobFileName: job.file_name,
                    successful: [],
                    failed: []
                });
            });

            const updateProgress = () => {
                onProgress((completedJobs / totalJobs) * 100);
            };

            if(!jobs.every(job => jobSubtitles[job.id])) {
                throw new Error('Subtitles not found for all jobs');
            }

            for (const job of jobs) {
                const subtitles = jobSubtitles[job.id];
                const jobResult = translationResults.get(job.id)!;

                for (const targetLanguage of targetLanguages) {
                    try {
                        const translatedTexts = await translate(
                            subtitles.map(subtitle => subtitle.text),
                            sourceLanguage,
                            targetLanguage,
                            (progress) => {
                                const individualContribution = progress / totalJobs;
                                const baseProgress = completedJobs / totalJobs;
                                onProgress((baseProgress + individualContribution) * 100);
                            }
                        );

                        const updatedSubtitles = subtitles.map((subtitle, index) => new Subtitle({
                            ...subtitle,
                            text: translatedTexts[index] || subtitle.text
                        }));

                        if(!signal.aborted) {
                            await addTranslation(job.id, updatedSubtitles, targetLanguage);
                            jobResult.successful.push({ language: targetLanguage });
                        }
                    } catch (error) {
                        jobResult.failed.push({
                            language: targetLanguage,
                            error: error instanceof Error ? error.message : 'Translation failed'
                        });
                    }

                    completedJobs++;
                    updateProgress();
                }
            }

            const results = Array.from(translationResults.values());
            const hasFailures = results.some(r => r.failed.length > 0);
            const hasSuccesses = results.some(r => r.successful.length > 0);

            resolve({
                results,
                hasFailures,
                hasSuccesses
            });

        } catch (error) {
            reject(error);
        }
    });
}

function createIframeWithHTML(htmlString: string, visible: boolean = false): HTMLIFrameElement {
    const iframe = document.createElement('iframe');
    iframe.className = `fixed top-0 left-0 w-full h-full ${visible ? '' : 'invisible'}`;
    document.body.appendChild(iframe);
    const iframeDoc = iframe.contentDocument || iframe.contentWindow!.document;
    iframeDoc.open();
    iframeDoc.write(htmlString);
    iframeDoc.close();
    return iframe;
}
